import 'core-js/es/array';
import 'core-js/es/symbol';
import 'core-js/es/array/includes';
import 'core-js/es/string/includes';
import '@/index.scss';

import type * as React from 'react';
import { useEffect, useMemo, useState } from 'react';

import { createRoot } from 'react-dom/client';

import {
  EVENT_AUTH_DISCARDED,
  EVENT_AUTH_EXPIRED,
  EVENT_AUTH_RENEW_ERROR,
  EVENT_AUTH_RENEW_SUCCESS,
  type SGWTConnectCore,
} from '@sgwt/connect-core';
import { IntlProvider } from 'react-intl';
import { Provider, useSelector } from 'react-redux';
import { throwError } from 'rxjs';

import { App } from '@/App/App';
import { logger } from '@/logging/logger';
import { connectToStreaming } from '@/services/streaming.service';
import { UserService } from '@/services/user.service';
import { setupFakeSGConnect, useFake } from '@/sgwtConnect';
import { navigableAsUsersLoadedAction, userLoadedAction } from '@/store/state/user/user.actions';
import { type AppDispatch, createStore, useAppDispatch } from '@/store/store';
import { CrashModal } from '@/App/utils/CrashModal';
import { SgwtContainer } from '@/SgwtContainer';
import { getConfig } from '@/config/config';
import { toLogEvents$ } from '@/services/streams.service';
import { streamingConnectedAction } from '@/store/state/streaming/streaming.actions';
import { toggleNavigateAsModalAction } from '@/store/state/ui/ui.actions';
import { messages } from '@/utils/locale';
import { useMatomo } from '@/utils/useMatomo';
import { ServiceLoader } from '@sgme/ui';
import { SgwtWebAnalytics, useSgwtWidgets } from '@sgwt/sgwt-widgets-react';
import { finalize, tap } from 'rxjs/operators';
import { selectCurrentLocale } from './store/state/ui/ui.selectors';
import { initSgBootstrap } from './utils/theme';
import { UserInfoContextProvider } from './UserInfoContextProvider';

//  █████╗ ██╗   ██╗████████╗██╗  ██╗ ██████╗ ██████╗ ██╗███████╗███████╗██████╗     ██╗   ██╗███████╗███████╗██████╗
// ██╔══██╗██║   ██║╚══██╔══╝██║  ██║██╔═══██╗██╔══██╗██║╚══███╔╝██╔════╝██╔══██╗    ██║   ██║██╔════╝██╔════╝██╔══██╗
// ███████║██║   ██║   ██║   ███████║██║   ██║██████╔╝██║  ███╔╝ █████╗  ██║  ██║    ██║   ██║███████╗█████╗  ██████╔╝
// ██╔══██║██║   ██║   ██║   ██╔══██║██║   ██║██╔══██╗██║ ███╔╝  ██╔══╝  ██║  ██║    ██║   ██║╚════██║██╔══╝  ██╔══██╗
// ██║  ██║╚██████╔╝   ██║   ██║  ██║╚██████╔╝██║  ██║██║███████╗███████╗██████╔╝    ╚██████╔╝███████║███████╗██║  ██║
// ╚═╝  ╚═╝ ╚═════╝    ╚═╝   ╚═╝  ╚═╝ ╚═════╝ ╚═╝  ╚═╝╚═╝╚══════╝╚══════╝╚═════╝      ╚═════╝ ╚══════╝╚══════╝╚═╝  ╚═╝

function initAppForAuthorizedUser() {
  const {
    piwik, // TODO: replace piwik by matomo
    sgconnect,
    env,
  } = getConfig();

  const sgconnectConfiguration = {
    cliendId: sgconnect.client_id,
    endpoint: sgconnect.authorization_endpoint,
    redirectUri: sgconnect.redirect_uri,
    scope: sgconnect.scope,
    acrValues: sgconnect.acr_values,
  };

  const analyticsConfiguration = piwik !== undefined ? { siteId: piwik.site_id, url: piwik.url } : undefined;

  initSgBootstrap();

  const rootElement = document.getElementById('root');

  if (!rootElement) {
    throw new Error('no root');
  }

  const root = createRoot(rootElement);

  root.render(
    // <React.StrictMode>
    <SgwtContainer sgConnect={sgconnectConfiguration} sgAnalytics={analyticsConfiguration} infrastructure="azure">
      <ApplicationWithStore />
    </SgwtContainer>,
    // </React.StrictMode>,
  );
}

function ApplicationWithStore() {
  const {
    piwik: { url, site_id }, // TODO: replace piwik by matomo
    env,
  } = getConfig();

  const { trackAction, trackRfq, updateSelectedCompanyName } = useMatomo();

  const store = useMemo(() => {
    return createStore(trackAction, trackRfq, updateSelectedCompanyName);
  }, [trackAction, trackRfq, updateSelectedCompanyName]); // never change -> see useMatomo comments

  return (
    <Provider store={store}>
      <SgwtWebAnalytics
        siteId={site_id}
        baseUrl={url}
        debug={env === 'devci'} // To show debug on homolo env
      />
      <UserInfoContextProvider>
        <ConnectedIntlProvider>
          <CrashModal />
          <Application />
        </ConnectedIntlProvider>
      </UserInfoContextProvider>
    </Provider>
  );
}

function ConnectedIntlProvider({ children }: React.PropsWithChildren<Record<string, unknown>>) {
  const locale = useSelector(selectCurrentLocale);

  return (
    <IntlProvider locale={locale} messages={messages[locale]}>
      {children}
    </IntlProvider>
  );
}

type ApplicationStatus = 'loading' | 'ready' | 'no-company' | 'not-allowed' | 'error';

function Application() {
  const dispatch = useAppDispatch();
  const { sgwtConnect } = useSgwtWidgets();

  const [status, setStatus] = useState<ApplicationStatus>('loading');

  useEffect(() => {
    if (sgwtConnect === undefined) {
      return;
    }

    subscribeToStreamEvents(dispatch);
    subscribeToSGConnectEvents(sgwtConnect.sgwtConnect);
  }, [sgwtConnect, dispatch]);

  useEffect(() => {
    if (sgwtConnect === undefined) {
      return;
    }

    if (useFake) {
      setupFakeSGConnect(sgwtConnect.sgwtConnect);
    }

    const subscription = UserService.getCurrentUser().subscribe(
      (user) => {
        if (!user.myFx) {
          setStatus('not-allowed');
          return;
        }

        if (user.companies.length === 0) {
          setStatus('no-company');
          return;
        }

        dispatch(userLoadedAction(user));

        // setupSgwtConnectWidget(sgwtConnect);

        if (!user.canNavigateAs) {
          setStatus('ready');
          return;
        }

        UserService.getNavigableAsUsers()
          .pipe(
            tap(() => dispatch(toggleNavigateAsModalAction())),
            finalize(() => setStatus('ready')),
          )
          .subscribe(
            (users) => dispatch(navigableAsUsersLoadedAction(users)),
            (err) => logger.logError('Error loading users', err),
          );
      },
      (error) => {
        if (error.status === 401 || error.status === 403) {
          setStatus('not-allowed');
        } else {
          setStatus('error');
        }

        return throwError(error);
      },
    );

    return () => {
      subscription.unsubscribe();
    };
  }, [sgwtConnect, dispatch]);

  if (sgwtConnect === undefined) {
    return <ServiceLoader />;
  }

  switch (status) {
    case 'loading':
      return <ServiceLoader />;
    case 'ready':
      return <App />;
    case 'no-company':
      return <NoCompany />;
    case 'not-allowed':
      return <NotAllowed />;
    case 'error':
      return <ErrorComponent />;
  }
}

function subscribeToStreamEvents(dispatch: AppDispatch) {
  connectToStreaming().then((streamingInfo) => {
    dispatch(streamingConnectedAction(streamingInfo));

    // logs some important events, useful for support
    toLogEvents$().subscribe((event) => logger.logInformation('Received event: {event}', event));
  });
}

function subscribeToSGConnectEvents(sgwtConnect: SGWTConnectCore) {
  sgwtConnect.on(EVENT_AUTH_RENEW_SUCCESS, () => logger.logInformation('sgwtConnect token renew'));
  sgwtConnect.on(EVENT_AUTH_DISCARDED, () => logger.logInformation('sgwtConnect token is no longer available on the client side.'));
  sgwtConnect.on(EVENT_AUTH_EXPIRED, () => logger.logInformation('sgwtConnect token is no longer valid.'));
  sgwtConnect.on(EVENT_AUTH_RENEW_ERROR, (error) => logger.logError('sgwtConnect failed to renew the token', JSON.stringify(error)));
}

function NoCompany() {
  return <div className="center-screen">{`Vous n'avez pas de société rattachée à votre compte`}</div>;
}

function NotAllowed() {
  location.href = window.sgmeConfiguration.redirect_uri;

  return <div className="center-screen">{`Vous n'avez pas les droits d'accéder à la plateforme`}</div>;
}

function ErrorComponent() {
  return <div className="center-screen">Une erreur est survenue sur la plateforme, nous vous invitons à réessayer plus tard</div>;
}

initAppForAuthorizedUser();
